import { useState, useEffect } from 'react';
import {
  Search, Loader2, AlertCircle, X, ArrowUpRight, ArrowDownLeft, CreditCard
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const Transactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  useEffect(() => { fetchTransactions(); }, []);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${BACKEND_URL}/api/transactions`, getAuthHeaders());
      setTransactions(res.data);
    } catch (err) { setError('Failed to fetch transactions'); }
    finally { setLoading(false); }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';
  const formatDateTime = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

  const filtered = transactions.filter(t => {
    if (typeFilter !== 'all' && t.type !== typeFilter) return false;
    const q = searchQuery.toLowerCase();
    if (!q) return true;
    return (
      (t.reference_label || '').toLowerCase().includes(q) ||
      (t.vendor_name || '').toLowerCase().includes(q) ||
      (t.customer_name || '').toLowerCase().includes(q) ||
      (t.payment_type || '').toLowerCase().includes(q) ||
      (t.reference_number || '').toLowerCase().includes(q) ||
      (t.description || '').toLowerCase().includes(q)
    );
  });

  const totalOut = filtered.filter(t => t.type === 'payment_out').reduce((s, t) => s + (t.amount || 0), 0);
  const totalIn = filtered.filter(t => t.type === 'payment_in').reduce((s, t) => s + (t.amount || 0), 0);

  return (
    <div data-testid="transactions-page">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight">Transactions</h1>
        <p className="text-slate-600 mt-1">All payment transactions history</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-sm border border-slate-200 p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <ArrowDownLeft className="h-5 w-5 text-green-600" />
            </div>
            <span className="text-sm font-medium text-slate-600">Total Received</span>
          </div>
          <span className="text-2xl font-bold text-green-600" data-testid="total-in">₹{totalIn.toLocaleString()}</span>
        </div>
        <div className="bg-white rounded-sm border border-slate-200 p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
              <ArrowUpRight className="h-5 w-5 text-red-600" />
            </div>
            <span className="text-sm font-medium text-slate-600">Total Paid Out</span>
          </div>
          <span className="text-2xl font-bold text-red-600" data-testid="total-out">₹{totalOut.toLocaleString()}</span>
        </div>
        <div className="bg-white rounded-sm border border-slate-200 p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <CreditCard className="h-5 w-5 text-blue-600" />
            </div>
            <span className="text-sm font-medium text-slate-600">Net</span>
          </div>
          <span className={`text-2xl font-bold ${(totalIn - totalOut) >= 0 ? 'text-green-600' : 'text-red-600'}`} data-testid="total-net">
            ₹{(totalIn - totalOut).toLocaleString()}
          </span>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-sm border border-slate-200 p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <input type="text" placeholder="Search by reference, vendor, customer, payment type..."
              value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} data-testid="search-input"
              className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
          </div>
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} data-testid="type-filter"
            className="px-4 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none">
            <option value="all">All Types</option>
            <option value="payment_in">Payment In</option>
            <option value="payment_out">Payment Out</option>
          </select>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-6">
          <AlertCircle className="h-5 w-5" /><span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto"><X className="h-4 w-4" /></button>
        </div>
      )}

      {/* Transactions Table */}
      <div className="bg-white rounded-sm border border-slate-200">
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16" data-testid="empty-state">
            <CreditCard className="h-16 w-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No transactions found</h3>
            <p className="text-slate-500">Payment transactions will appear here once recorded via Payment In or Payment Out</p>
          </div>
        ) : (
          <>
            <div className="hidden md:grid grid-cols-9 gap-4 px-6 py-4 border-b border-slate-200 bg-slate-50">
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Date</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Type</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Reference</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Party</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Ref No.</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Payment Mode</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Amount</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider col-span-2">Description</div>
            </div>
            {filtered.map((txn) => (
              <div key={txn.id} data-testid={`txn-row-${txn.id}`}
                className="grid grid-cols-1 md:grid-cols-9 gap-4 px-6 py-4 border-b border-slate-100 hover:bg-slate-50 items-center">
                <div className="text-slate-600 text-sm">{txn.date ? formatDate(txn.date) : formatDateTime(txn.created_at)}</div>
                <div>
                  {txn.type === 'payment_in' ? (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-700">
                      <ArrowDownLeft className="h-3 w-3" /> In
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-700">
                      <ArrowUpRight className="h-3 w-3" /> Out
                    </span>
                  )}
                </div>
                <div className="font-mono text-sm font-medium text-slate-900">{txn.reference_label || '-'}</div>
                <div className="text-slate-700 text-sm">{txn.vendor_name || txn.customer_name || '-'}</div>
                <div className="text-slate-600 text-sm font-mono">{txn.reference_number || '-'}</div>
                <div>
                  <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-700">
                    {txn.payment_type}
                  </span>
                </div>
                <div className={`font-medium ${txn.type === 'payment_in' ? 'text-green-600' : 'text-red-600'}`}>
                  {txn.type === 'payment_in' ? '+' : '-'} ₹{txn.amount?.toLocaleString()}
                </div>
                <div className="text-slate-500 text-sm truncate col-span-2">{txn.description || txn.notes || '-'}</div>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
};
